import { useEffect, useState } from 'react';
import Popup from 'devextreme-react/popup';
import DataGrid, { Column, SearchPanel, Scrolling, Paging, Pager } from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import { connection } from '../../../../signalR/Controllers/Analyzers';
import moment from 'moment';
import { useApiOrders } from '../../../../api/api.orders';
import { useApiAnalyzer } from '../../../../api/api.analyzers';
import QcReceptionsPanel from './QcReceptionsPanel';
import { useAppSelector } from '../../../../contexts/hooks';
import { selectModuleQC } from '../../../../contexts/selectors';
import './ModalTracerByAnalyzer.scss';
import { useScreenSize } from '../../../../utils/media-query';

interface Props {
  analyzer: any;
  orientation: 'horizontal' | 'vertical';
  onClose?: () => void;
}

const ModalTracerByAnalyzer: React.FC<Props> = ({ analyzer, onClose, orientation }) => {
  const { getTrace } = useApiAnalyzer();
  const { findOrder } = useApiOrders();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [allRecords, setAllRecords] = useState<any[]>([]);
  const [loadingProgress, setLoadingProgress] = useState<string>('');  
  const { isXSmall, isSmall } = useScreenSize();

  // El control de calidad se ofrece aqui con el mismo criterio que en la pantalla de
  // Analizadores: modulo encendido y permiso de consulta. Sin el permiso los endpoints
  // responden 403, asi que mostrar la pestana solo serviria para chocar contra el.
  const moduleQC: boolean = useAppSelector(selectModuleQC);
  const Prm_QC_R: boolean = useAppSelector((x) => Boolean(x.userInfo.Permissions?.Prm_QC_R));
  const puedeVerQC: boolean = moduleQC && Prm_QC_R;

  useEffect(() => {
    if (!analyzer) return;

    const loadAllData = async () => {
      setLoading(true);
      setAllRecords([]);
      setTotalRecords(0);
      setLoadingProgress('Iniciando carga...');

      try {
        // Cargar primera página para conocer el total
        const firstPage = await getTrace(analyzer.Az_Id, 1);

        setTotalRecords(firstPage.TotalRecords);

        let allData = [...firstPage.Records]; // Si hay más páginas, cargarlas todas
        if (firstPage.TotalPages > 1) {
          const totalPages = firstPage.TotalPages;
          setLoadingProgress(`Cargando página 1 de ${totalPages}...`);

          // Cargar todas las páginas restantes
          for (let page = 2; page <= totalPages; page++) {
            setLoadingProgress(`Cargando página ${page} de ${totalPages}...`);

            const pageData = await getTrace(analyzer.Az_Id, page);

            allData = [...allData, ...pageData.Records]; // Acumular datos correctamente
          }
        }

        // Ordenar todos los registros por fecha más reciente primero
        const sortedRecords = allData.sort((a: any, b: any) => {
          return new Date(b.Fecha).getTime() - new Date(a.Fecha).getTime();
        });

        setAllRecords(sortedRecords);
        setLoadingProgress('');
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setLoadingProgress('Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };

    loadAllData();

    // SignalR para actualizaciones en tiempo real
    const resultEvent = `AnalyzerResult_${analyzer.Az_Id}`;

    const handleNewResult = (data: any) => {      
      // Insertar al inicio y mantener orden por fecha
      setAllRecords((prev) => {
        const newRecords = [data, ...prev];
        return newRecords.sort((a: any, b: any) => {
          return new Date(b.Fecha).getTime() - new Date(a.Fecha).getTime();
        });
      });
      // Actualizar el total
      setTotalRecords((prev) => prev + 1);
    };

    if (connection) {
      connection.on(resultEvent, handleNewResult);
    }

    return () => {
      if (connection) {
        connection.off(resultEvent, handleNewResult);
      }
    };
  }, [analyzer]);

  const handleItemClick = (e: any) => {
    const { IdOrden } = e.data;
    if (!IdOrden) return;
    setLoading(true);
    findOrder(undefined, IdOrden, setLoading, true);
  };

  const formatColombianTime = (dateString: string) => {
    try {
      const cleanedDate = dateString.replace(/\.\d+/, '');
      const momentDate = moment(cleanedDate);
      if (!momentDate.isValid()) {
        return 'Fecha inválida';
      }
      return momentDate.format('hh:mm A');
    } catch (error) {
      return 'Error fecha';
    }
  };

  const formatFullDate = (dateString: string) => {
    try {
      const cleanedDate = dateString.replace(/\.\d+/, '');
      const momentDate = moment(cleanedDate);
      if (!momentDate.isValid()) {
        return 'Fecha inválida';
      }
      return momentDate.format('DD/MM/YYYY');
    } catch (error) {
      return 'Error fecha';
    }
  };

  // Mismas palabras que el estado de una corrida de control: lo que entra por el equipo se
  // procesa o se rechaza, y decirlo de dos maneras distintas en pestanas vecinas solo obliga
  // a traducir mentalmente entre una y otra.
  const renderStatusCell = (data: any) => {
    const isSuccess = data.value;
    return <div className={`status-chip ${isSuccess ? 'success' : 'error'}`}>{isSuccess ? 'Procesado' : 'Rechazado'}</div>;
  };

  const renderDateCell = (data: any) => {
    return (
      <div className='date-cell'>
        <div className='date-main'>{formatFullDate(data.value)}</div>
        <div className='time-sub'>{formatColombianTime(data.value)}</div>
      </div>
    );
  };

  const renderOrderCell = (data: any) => {
    return (
      <div className='order-cell'>
        <div className='order-main'>{data.value || 'N/A'}</div>
      </div>
    );
  };

  const renderAnaliteCell = (data: any) => {
    return (
      <div className='analite-cell'>
        <div className='analite-main'>{data.value || 'N/A'}</div>
        <div className='analite-sub'>{data.data.Message || 'Sin resultado'}</div>
      </div>
    );
  };

  const itemRender = () => {
    if (loading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <LoadIndicator visible={true} />
          <div style={{ marginTop: '1rem' }}>{loadingProgress || 'Cargando resultados...'}</div>
        </div>
      );
    }

    if (allRecords.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <h3>No hay resultados</h3>
          <p>No se encontraron resultados para este analizador.</p>
        </div>
      );
    }
    return (
      <div className='analyzer-results'>
        <div className='analyzer-results-summary'>
          <span className='summary-count'>Total: {totalRecords > 0 ? totalRecords.toLocaleString() : '0'}</span>
        </div>
        <DataGrid dataSource={allRecords} showBorders={false} showRowLines={true} showColumnLines={false} rowAlternationEnabled={true} hoverStateEnabled={true} onRowClick={handleItemClick} height='100%' columnAutoWidth={true} wordWrapEnabled={true}>
          <SearchPanel visible={true} placeholder='Buscar resultados...' />
          <Scrolling mode='standard' />
          <Paging enabled={true} defaultPageSize={50} />
          <Pager visible={true} allowedPageSizes={[25, 50, 100, 200, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />

          <Column dataField='Success' caption='Estado' width={90} cellRender={renderStatusCell} allowSorting={true} allowFiltering={false} />

          <Column dataField='Fecha' caption='Fecha' width={140} cellRender={renderDateCell} allowSorting={true} sortOrder='desc' allowFiltering={false} />

          <Column dataField='Order' caption='Orden' width={150} cellRender={renderOrderCell} allowSorting={true} />

          <Column dataField='Analite' caption='Analito' cellRender={renderAnaliteCell} allowSorting={true} />
        </DataGrid>
      </div>
    );
  };

  if (orientation === 'vertical') {
    return <>{itemRender()}</>;
  }

  return (
    <Popup 
      visible={!!analyzer} 
      onHiding={onClose} 
      fullScreen={isSmall || isXSmall} 
      title={puedeVerQC ? analyzer.Az_Nombre : `Resultados de ${analyzer.Az_Nombre}`} 
      width={puedeVerQC ? 980 : 800} 
      maxWidth='95vw'
      height={isSmall || isXSmall ? '100%' : '90vh'} 
      showCloseButton
      wrapperAttr={{ class: 'analyzer-modal-wrapper' }}
    >
      {puedeVerQC ? (
        // Mismas dos pestanas que la pantalla de Analizadores, y en el mismo orden: quien
        // abre el analizador desde el inicio ve lo que esta llegando por el equipo sin tener
        // que cambiar de pantalla. deferRendering deja la consulta de corridas para cuando
        // se entra a la pestana; abrir el modal no debe costar dos consultas.
        <div className='analyzer-modal-tabs'>
          <TabPanel className='analyzer-tab-panel' animationEnabled={true} swipeEnabled={false} deferRendering={true}>
            <TabItem title='Resultados'>
              <div className='tab-content'>{itemRender()}</div>
            </TabItem>
            <TabItem title='Control de calidad'>
              <div className='tab-content'>
                <QcReceptionsPanel analyzer={analyzer} onNavigate={onClose} />
              </div>
            </TabItem>
          </TabPanel>
        </div>
      ) : (
        itemRender()
      )}
    </Popup>
  );
};

export default ModalTracerByAnalyzer;
